import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #mobileFocusController;
  #searchController;
  #mobileScrollPositions = new WeakMap();
  #mobileMedia = window.matchMedia('(max-width: 749px)');

  #onBreakpointChange = (event) => {
    if (event.matches || !this.classList.contains('header__drawer--mobile')) return;
    this.#mobileFocusController?.abort();
    if (this.isOpen) this.close();
  };

  get #isMobileDrawer() {
    return this.classList.contains('header__drawer--mobile') && window.matchMedia('(max-width: 749px)').matches;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#mobileMedia.addEventListener('change', this.#onBreakpointChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#mobileMedia.removeEventListener('change', this.#onBreakpointChange);
    this.#mobileFocusController?.abort();
    this.#searchController?.abort();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (this.#isMobileDrawer && details !== this.refs.details) {
      this.#mobileScrollPositions.set(details, this.querySelector('.menu-drawer__mobile-scroll')?.scrollTop ?? 0);
    }
    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      if (this.#isMobileDrawer) {
        requestAnimationFrame(async () => {
          const panel = details === this.refs.details
            ? this.querySelector('.menu-drawer')
            : details.querySelector('.menu-drawer__submenu');
          if (panel) await onAnimationEnd(panel, undefined, { subtree: false });
          if (this.isOpen && details.open && details.classList.contains('menu-open')) this.#trapDrawerFocus(details);
        });
        return;
      }
      setTimeout(() => {
        if (this.isOpen) this.#trapDrawerFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  openSearch() {
    const search = document.querySelector('#search-modal');
    const dialog = search?.querySelector('dialog');
    if (!search?.showDialog || !dialog) return;

    this.#mobileFocusController?.abort();
    removeTrapFocus();
    this.#searchController?.abort();
    this.#searchController = new AbortController();
    dialog.addEventListener('toggle', () => {
      if (!dialog.open && this.isOpen && this.#isMobileDrawer) {
        document.documentElement.setAttribute('scroll-lock', '');
      }
    }, { signal: this.#searchController.signal });
    dialog.addEventListener('close', () => {
      if (!this.isOpen || !this.#isMobileDrawer) return;
      document.documentElement.setAttribute('scroll-lock', '');
      this.#trapDrawerFocus(this.refs.details, this.querySelector('.menu-drawer__search-link'));
    }, { once: true, signal: this.#searchController.signal });
    search.showDialog();
  }

  #trapDrawerFocus(details, focusTarget) {
    if (!this.#isMobileDrawer) {
      trapFocus(details);
      return;
    }

    removeTrapFocus();
    this.#mobileFocusController?.abort();
    this.#mobileFocusController = new AbortController();
    const { signal } = this.#mobileFocusController;
    const container = details === this.refs.details
      ? this.querySelector('.menu-drawer')
      : details.querySelector('.menu-drawer__submenu') ?? details;
    if (!container) return;

    const focusableElements = () => Array.from(container.querySelectorAll(
      'summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]'
    )).filter((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || document.querySelector('dialog[open]')) return;
      const elements = focusableElements();
      const first = elements[0];
      const last = elements.at(-1);
      if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !container.contains(document.activeElement))) {
        event.preventDefault();
        first?.focus();
      }
    }, { capture: true, signal });
    document.addEventListener('focusin', (event) => {
      if (!container.contains(event.target) && !document.querySelector('dialog[open]')) {
        focusableElements()[0]?.focus({ preventScroll: true });
      }
    }, { capture: true, signal });
    (focusTarget ?? focusableElements()[0])?.focus({ preventScroll: true });
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (this.#isMobileDrawer && this.#mobileScrollPositions.has(details)) {
        const scroll = this.querySelector('.menu-drawer__mobile-scroll');
        if (scroll) scroll.scrollTop = this.#mobileScrollPositions.get(details);
        this.#mobileScrollPositions.delete(details);
      }

      if (details === this.refs.details) {
        this.#mobileFocusController?.abort();
        this.#searchController?.abort();
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
        if (this.#isMobileDrawer) summary.focus({ preventScroll: true });
      } else {
        setTimeout(() => {
          if (!this.isOpen) return;
          const parentDetails = this.#isMobileDrawer
            ? details.parentElement?.closest('details.menu-open') ?? this.refs.details
            : this.refs.details;
          this.#trapDrawerFocus(parentDetails, this.#isMobileDrawer ? summary : undefined);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  const summary = element.querySelector('summary');
  if (element.closest('header-drawer')?.classList.contains('header__drawer--mobile') && !summary?.hasAttribute('on:click')) {
    summary?.removeAttribute('aria-expanded');
  } else {
    summary?.setAttribute('aria-expanded', 'false');
  }
}
